import React, { createContext, useState, useContext, useEffect } from 'react';
import nodeApi from '../services/nodeApi';

// Create auth context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // For demo purposes, we'll just check if there's a token
          // In a real app, you would validate the token with the backend
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          setUser(userData);
        }
      } catch (err) {
        console.error('Error checking authentication:', err);
        // If there's an error, clear the token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkLoggedIn();
  }, []);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      // For demo purposes, simulate a successful login
      // This is a temporary solution until the backend API is fully implemented
      console.log('Login attempt:', { email, password });

      // Create a mock response
      const mockResponse = {
        user: {
          id: 'demo-user-' + Date.now(),
          name: 'Demo User',
          email: email,
          stakeholderType: 'trader' // Default stakeholder type
        },
        token: 'demo-token-' + Date.now()
      };

      // Store the mock token and user
      localStorage.setItem('token', mockResponse.token);
      localStorage.setItem('user', JSON.stringify(mockResponse.user));
      setUser(mockResponse.user);

      // When the real API is ready, uncomment this code:
      /*
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      return response.data;
      */

      return mockResponse;
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid email or password. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register function with stakeholder type
  const register = async (userData) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Registration data:', userData);

      // Send the registration data to the Node.js API
      const response = await nodeApi.register(userData);

      console.log('Registration response:', response);

      if (!response || !response.success) {
        const errorMessage = response ? response.message : 'No response from server';
        console.error('Registration failed:', errorMessage);
        setError(errorMessage || 'Registration failed. Please try again or contact support.');
        return null;
      }

      // Create a response object
      const authResponse = {
        user: {
          id: response.registrationId || ('user-' + Date.now()),
          name: `${userData.firstName} ${userData.lastName}`,
          email: userData.email,
          stakeholderType: userData.stakeholderType
        },
        token: 'token-' + Date.now()
      };

      // Store the token and user
      localStorage.setItem('token', authResponse.token);
      localStorage.setItem('user', JSON.stringify(authResponse.user));
      setUser(authResponse.user);

      // Return the auth response
      return authResponse;
    } catch (err) {
      // If something fails, show error but don't redirect
      console.error('Registration failed:', err);
      setError('Registration failed. Please try again or contact support.');

      // Don't redirect automatically - let the user try again
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
